/**
 * GroupBy Chart Widget - Muestra datos agrupados en gráfico
 *
 * Widget para mostrar métricas agrupadas (pie/bar chart).
 */

import React from 'react';
import { Box, Typography, LinearProgress, Stack } from '@mui/material';
import { WidgetWrapper } from './WidgetWrapper';
import {useChartPalette} from "../../../theme/chartPalette";

export interface GroupByData {
  groupKey: string | null;
  groupLabel?: string;
  count: number;
}

export interface GroupByChartProps {
  title: string;
  data: GroupByData[];
  loading?: boolean;
  computing?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  icon?: React.ReactNode;
  showPercentage?: boolean;
  maxItems?: number;
}

// Colores para las barras (Material Design palette)

export const GroupByChart: React.FC<GroupByChartProps> = ({
  title,
  data,
  loading = false,
  computing = false,
  error = null,
  onRefresh,
  icon,
  showPercentage = true,
  maxItems = 5,
}) => {
  const paleta = useChartPalette();
  // Calcular total y limitar items
  const displayData = data.slice(0, maxItems);
  const total = data.reduce((sum, item) => sum + item.count, 0);

  const getLabel = (item: GroupByData): string => {
    if (item.groupLabel) return item.groupLabel;
    if (item.groupKey === null) return '(Sin valor)';
    return String(item.groupKey);
  };

  return (
    <WidgetWrapper
      title={title}
      loading={loading}
      computing={computing}
      error={error}
      onRefresh={onRefresh}
      icon={icon}
      minHeight={200}
    >
      <Box sx={{ width: '100%', px: 2 }}>
        {displayData.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No hay datos disponibles
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {displayData.map((item, index) => {
              const percentage = total > 0 ? (item.count / total) * 100 : 0;
              const color = paleta.color(index);

              return (
                <Box key={item.groupKey ?? `null-${index}`}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '60%',
                      }}
                    >
                      {getLabel(item)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.count.toLocaleString()}
                      {showPercentage && ` (${percentage.toFixed(1)}%)`}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: color,
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              );
            })}

            {data.length > maxItems && (
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
              >
                +{data.length - maxItems} más
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    </WidgetWrapper>
  );
};

export default GroupByChart;
